import { Button } from '@/components/ui/button';
import DataDrivenField from './DataDrivenField';
import { Form } from '@/components/ui/form';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { buildFormSchema } from '@/app/IntegrationHub/buildFormSchema';
import { IntegrationLimitedDto } from '@/lib/integrationTypes/integrationTypes';
import { useCopyToClipboard } from '@/lib/clipboard';
import { IntegrationScriptLogDto } from '@/lib/api/types.gen';
import { IntegrationLogs } from './IntegrationLogs';
import { Loader2 } from 'lucide-react';

interface DeviceVariablesFormProps {
  integration: IntegrationLimitedDto;
  onVariableSubmit: SubmitHandler<FieldValues>;
  onFormChange?: (data: FieldValues) => void;
  hideSubmit?: boolean;
  submitText?: string;
  currentlyProcessing?: boolean;
  logs?: IntegrationScriptLogDto[] | null;
  border?: boolean;
  defaultValues?: FieldValues;
  routeNamespaceId?: string;
  tenantId?: string;
}

export default function DeviceVariablesForm({
  logs,
  integration,
  onVariableSubmit,
  hideSubmit = false,
  onFormChange,
  submitText,
  currentlyProcessing,
  border,
  defaultValues,
  routeNamespaceId,
  tenantId,
}: DeviceVariablesFormProps) {
  interface FormValues {
    [key: string]: string | number | boolean | string[] | number[] | boolean[] | null | undefined;
  }

  const form = useForm<FormValues>(buildFormSchema(integration, defaultValues) as any);
  const [copy, isCopied] = useCopyToClipboard(() => {
    const values = form.getValues() as FormValues;

    return integration.integrationFields
      .reduce((arr, entry) => {
        const raw = values[entry.id as keyof typeof values];
        const value = Array.isArray(raw) ? raw.join(', ') : (raw as any);

        if (value == null) return arr;

        return [...arr, `${entry.name}\n${value}`];
      }, [] as string[])
      .join('\n\n');
  });

  form.watch((data) => {
    if (onFormChange) {
      onFormChange(data as FieldValues);
    }
  });

  if (!integration.integrationFields || integration.integrationFields.length === 0) {
    return (
      <section className="rounded-xl border bg-[#111]">
        <div className="p-6">
          <h2 className="pb-6 text-xl font-semibold">Configure {integration.name}</h2>
          <p className="text-gray-400">
            No configuration options connected. The integration likely isn't filled out, or
            something has gone wrong.
          </p>
        </div>
      </section>
    );
  }

  const fields = [];

  for (const field of integration.integrationFields) {
    fields.push(
      <DataDrivenField
        integrationField={field}
        form={form}
        key={field.id}
        routeNamespaceId={routeNamespaceId}
        tenantId={tenantId}
      />
    );
  }

  return (
    <section
      className={`space-y-4 rounded-xl  ${border === undefined || border === true ? 'border' : ''} bg-card2`}
    >
      <div className="flex items-center justify-between border-b w-full p-4">
        <h1 className="font-semibold">{integration.name} Settings</h1>
        <Button variant="outline" size="sm" onClick={() => copy()} disabled={isCopied}>
          {isCopied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <div>
        {integration.description ? (
          <h3 className="mt-6 text-lg font-semibold">{integration.description}</h3>
        ) : null}
        <Form {...form}>
          <form
            id="device-variables-form"
            onSubmit={form.handleSubmit(onVariableSubmit)}
            className="space-y-4 p-4 pt-0"
            autoComplete="off"
          >
            {fields}
          </form>
        </Form>
        {logs ? <IntegrationLogs logs={logs} /> : undefined}
        {!hideSubmit && (
          <div className="flex justify-end p-4 pt-0">
            <Button type="submit" form="device-variables-form" size="sm" disabled={currentlyProcessing} className="gap-2">
              {currentlyProcessing && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitText ?? (currentlyProcessing ? 'Connecting...' : 'Connect')}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
